import { QueryClient } from '@tanstack/react-query';

/**
 * Shared TanStack Query client for the whole app.
 *
 * ONE client lives at the app root and is passed via <QueryClientProvider>
 * in main.jsx. Every useQuery / useMutation hook reads from it, so cache
 * hits are shared across pages / components. Do not create a second one.
 *
 * Defaults chosen for THIS app:
 *
 *   staleTime: 60_000 (1 min)
 *     Reservations, groups, and availability data don't change second-
 *     by-second on the server. A one-minute freshness window is a big
 *     win for perceived speed (revisiting a page in the same session is
 *     a cache hit) without letting genuinely stale UI linger.
 *
 *   gcTime: 5 * 60_000 (5 min)
 *     How long inactive queries linger in the cache before eviction.
 *     Five minutes matches a typical user session hop pattern
 *     (browse → detail → back → different detail) so returning to a
 *     page still hits cache.
 *
 *   retry: 1
 *     Network glitches deserve one retry; anything beyond that is
 *     probably a real error the user should see. The backend's
 *     4xx responses (403 forbidden, 404 not found, 422 validation)
 *     are surfaced immediately — retry is only useful for transient
 *     network failures.
 *
 *   refetchOnWindowFocus: false
 *     The default (true) is nice for dashboards but distracting for
 *     forms mid-edit. Individual hooks can opt in per resource.
 *
 * Every server-state discipline in this app is documented in
 * docs/frontend/server-state-migration.md — read that FIRST before
 * changing anything here.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      gcTime: 5 * 60_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      // Mutations don't retry — a duplicate write is worse than a
      // visible error. Explicit .retry() call sites can opt in.
      retry: 0,
    },
  },
});
